// src/components/SupportChatbot.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Truck, ShoppingCart, User as UserIcon, RefreshCcw, ArrowRight, Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { chatWithSupport, ChatMessage, ChatAction } from '../services/geminiService';
import { useCoreContext } from '../../../lib/coreContext';
import type { Order, User } from '@/types';
import { apiRequest } from '../../../lib/api';
import { API_PATHS } from '../../../lib/apiRoutes';
import {
  SUPPORT_CONDUCT_CUSTOMER_SUMMARY,
  SUPPORT_TALK_TO_TEAMMATE_MESSAGE,
  isSupportHumanRequest
} from '../../support/lib/supportConduct';
import { FEATURE_ROUTES } from '../../../app/routeConstants';
import { useBodyScrollLock } from '../../../lib/useBodyScrollLock';
import { useFocusTrap } from '../../../lib/useFocusTrap';

interface BrowserSpeechRecognitionEvent {
  readonly results: {
    readonly [index: number]: {
      readonly [index: number]: {
        readonly transcript: string;
      };
    };
  };
}

interface BrowserSpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognitionInstance;

type WindowWithSpeech = Window & {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
};

interface SupportChatbotProps {
  currentUser?: User | null;
  recentOrders?: Order[];
  isOpen?: boolean;
  onClose?: () => void;
  showFloatingButton?: boolean;
}

export const SupportChatbot: React.FC<SupportChatbotProps> = ({
  currentUser,
  recentOrders = [],
  isOpen: controlledIsOpen,
  onClose,
  showFloatingButton = true
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  
  const setIsOpen = useCallback((val: boolean) => {
    if (!val) {
      if (onClose) onClose();
      setInternalIsOpen(false);
    } else {
      setInternalIsOpen(true);
    }
  }, [onClose]);

  useBodyScrollLock(isOpen);

  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(isOpen, panelRef, () => setIsOpen(false));

  const escalatingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, setIsOpen]);

  const [messages, setMessages] = useState<(ChatMessage & { actions?: ChatAction[] })[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef<BrowserSpeechRecognitionInstance | null>(null);
  const navigate = useNavigate();
  const [faqChips, setFaqChips] = useState<Array<{ question: string; answer: string }>>([]);
  const [faqError, setFaqError] = useState<string | null>(null);
  const [faqReloadKey, setFaqReloadKey] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const core = useCoreContext();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    const role = currentUser?.role || 'guest';
    setFaqError(null);
    // Fetching from /faq path
    void apiRequest<{ data?: Array<{ question?: string; answer?: string }> }>(
      `${API_PATHS.support.faq}?role=${encodeURIComponent(role)}`
    )
      .then(payload => {
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        setFaqChips(
          rows
            .map(row => ({ question: String(row.question || '').trim(), answer: String(row.answer || '').trim() }))
            .filter(row => row.question && row.answer)
            .slice(0, 6)
        );
        setFaqError(null);
      })
      .catch(() => {
        setFaqChips([]);
        setFaqError('Couldn’t load suggested questions.');
      });
  }, [isOpen, currentUser?.role, faqReloadKey]);

  const escalateToHuman = async (reason?: string) => {
    if (escalatingRef.current) return;
    escalatingRef.current = true;
    setIsLoading(true);
    try {
      const activeOrderId = recentOrders[0]?.id;
      const firstUserMsg = messages.find(m => m.role === 'user')?.message || reason || 'Customer requested operator assistance';
      if (!currentUser) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'agent',
            message: 'Sign in so a live support representative can assist you.',
            timestamp: new Date().toISOString()
          }
        ]);
        return;
      }
      await apiRequest(API_PATHS.support.tickets, {
        method: 'POST',
        body: JSON.stringify({
          subject: `Chat Help: ${firstUserMsg.slice(0, 50)}`,
          body: messages.map(m => `[${m.role.toUpperCase()}]: ${m.message}`).join('\n') || firstUserMsg,
          category: 'general',
          priority: 'normal',
          orderRef: activeOrderId
        })
      });
      const escalationMsg: ChatMessage = {
        role: 'agent',
        message: 'Connecting you with a live support representative. You can keep messaging here — an agent has been notified and will reply shortly.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, escalationMsg]);
    } catch {
      const escalationMsg: ChatMessage = {
        role: 'agent',
        message: 'We could not connect to a live representative just now. Please try sending a message again, or email support@ninposnacks.com.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, escalationMsg]);
    } finally {
      escalatingRef.current = false;
      setIsLoading(false);
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      message: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const userContext = currentUser ? {
        userId: currentUser.id || currentUser._id,
        username: currentUser.username,
        creditBalance: currentUser.creditBalance || 0,
        membershipTier: currentUser.tier || 'COMMON',
        recentOrders: recentOrders.slice(0, 3).map(order => ({
          id: order.id,
          status: order.status,
          amount: order.amount,
          createdAt: order.createdAt
        }))
      } : {};

      const response = await chatWithSupport(text, messages, userContext);

      const agentMessage: ChatMessage & { actions?: ChatAction[] } = {
        role: 'agent',
        message: response.reply,
        timestamp: response.timestamp,
        actions: response.actions
      };

      setMessages(prev => [...prev, agentMessage]);
      if (response.escalateSuggested) {
        await escalateToHuman();
      }
    } catch {
      const errorMessage: ChatMessage = {
        role: 'agent',
        message: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    await sendMessage(input);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    if (!showFloatingButton) return null;
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-[120px] right-10 bg-zinc-900 border border-zinc-800 hover:border-ninpo-lime text-ninpo-lime rounded-full p-4 shadow-neon z-[10000] transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="Open chat support"
      >
        <svg className="w-7 h-7 transform group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Ninpo Snacks support chat"
      tabIndex={-1}
      className={`fixed inset-x-0 bottom-0 md:inset-auto md:bottom-28 md:right-10 w-full h-[85vh] md:w-[24rem] md:h-[32rem] bg-zinc-900/95 backdrop-blur-xl md:rounded-2xl shadow-2xl flex flex-col z-[10000] border-t md:border transition-all duration-500 animate-in slide-in-from-bottom-10 fade-in ${
      isLoading
        ? 'border-cyan-400/60 shadow-[0_0_25px_rgba(6,182,212,0.4)] animate-pulse'
        : 'border-white/10'
    }`}
    >
      {/* Header */}
      <div className="bg-black/50 border-b border-white/5 text-white p-4 md:rounded-t-2xl flex justify-between items-center backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-zinc-800 border border-white/10 overflow-hidden shadow-inner">
            <svg className="w-4 h-4 text-ninpo-lime" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-sm tracking-wide">NinpoSnacks Support</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 bg-ninpo-lime rounded-full animate-pulse shadow-[0_0_8px_rgba(132,204,22,0.8)]" />
              <span className="text-[10px] text-zinc-400 uppercase tracking-widest">Online</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-zinc-400 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
          aria-label="Close chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
        {messages.length === 0 && (
          <div className="text-center text-zinc-400 mt-12 px-6 space-y-4">
            <div>
              <div className="w-16 h-16 bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/5">
                <span className="text-2xl">👋</span>
              </div>
              <p className="text-sm text-zinc-200 mb-2 font-medium">Hi! How can I help you today?</p>
              <p className="text-xs text-zinc-500 leading-relaxed">{SUPPORT_CONDUCT_CUSTOMER_SUMMARY}</p>
              <a
                href={FEATURE_ROUTES.legal.codeOfConduct}
                className="inline-block mt-2 text-[10px] uppercase tracking-widest text-ninpo-lime/80 hover:text-ninpo-lime"
              >
                Support &amp; Conduct Policy
              </a>
            </div>

            {faqError ? (
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-[10px] uppercase tracking-widest text-amber-200/90">{faqError}</p>
                <button
                  type="button"
                  onClick={() => setFaqReloadKey((k) => k + 1)}
                  className="text-[10px] font-black uppercase tracking-widest text-ninpo-lime hover:underline"
                >
                  Retry suggestions
                </button>
              </div>
            ) : null}

            {faqChips.length > 0 ? (
              <div className="flex flex-wrap gap-2 justify-center">
                {faqChips.map(chip => (
                  <button
                    key={chip.question}
                    type="button"
                    onClick={() => void sendMessage(chip.question)}
                    className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 bg-white/5 hover:border-ninpo-lime/40 text-zinc-200"
                  >
                    {chip.question}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => void sendMessage('Where is my order?')}
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 bg-white/5 hover:border-ninpo-lime/40 text-zinc-200 transition-colors"
                >
                  Where is my order?
                </button>
                <button
                  type="button"
                  onClick={() => void sendMessage('Check my wallet balance')}
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 bg-white/5 hover:border-ninpo-lime/40 text-zinc-200 transition-colors"
                >
                  Wallet Balance
                </button>
                <button
                  type="button"
                  onClick={() => void sendMessage('How do bottle returns work?')}
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 bg-white/5 hover:border-ninpo-lime/40 text-zinc-200 transition-colors"
                >
                  Bottle Returns
                </button>
              </div>
            )}
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                msg.role === 'user'
                  ? 'bg-ninpo-lime text-black rounded-br-sm'
                  : 'bg-zinc-800 text-zinc-100 border border-white/5 rounded-bl-sm'
              }`}
            >
              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.message}</p>

              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-4 flex flex-col gap-2 pt-3 border-t border-white/5">
                  {msg.actions.map((action, actionIdx) => (
                    <button
                      key={actionIdx}
                      onClick={() => {
                        const orderId = String(action.params?.orderId || '').trim();
                        switch (action.action) {
                          case 'track_order':
                            if (orderId) {
                              navigate(`/orders/${encodeURIComponent(orderId)}/track`);
                            } else {
                              navigate('/account');
                            }
                            break;
                          case 'view_inventory':
                            navigate('/shop');
                            break;
                          case 'request_refund':
                            if (orderId) {
                              navigate(`/orders/${encodeURIComponent(orderId)}/track`);
                            } else {
                              navigate('/account');
                            }
                            break;
                          case 'view_profile':
                            navigate('/account');
                            break;
                          default:
                            break;
                        }
                      }}
                      className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5 group text-left"
                    >
                      <div className="flex items-center gap-2.5">
                        {action.action === 'track_order' && <Truck className="w-3.5 h-3.5" />}
                        {action.action === 'view_inventory' && <ShoppingCart className="w-3.5 h-3.5" />}
                        {action.action === 'view_profile' && <UserIcon className="w-3.5 h-3.5" />}
                        {action.action === 'request_refund' && <RefreshCcw className="w-3.5 h-3.5" />}
                        <span className="text-[11px] font-black uppercase tracking-wider">{action.label}</span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-zinc-600 group-hover:text-ninpo-lime transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {msg.timestamp && (
              <span className="text-[10px] text-zinc-500 mt-1.5 px-1 font-medium">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start animate-in fade-in duration-300">
            <div className="bg-zinc-800 text-zinc-300 border border-white/5 rounded-2xl rounded-bl-sm px-5 py-4 shadow-sm flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-ninpo-lime rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-ninpo-lime rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-ninpo-lime rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} className="h-2" />
      </div>

      {/* Neural Waveform & Input */}
      <div className="p-4 border-t border-white/10 bg-black/40 md:rounded-b-2xl backdrop-blur-md space-y-3">
        {isListening && (
          <div className="flex items-center justify-center gap-1.5 py-2 px-4 rounded-xl bg-cyan-950/40 border border-cyan-500/30 animate-pulse">
            <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400 mr-2">Listening...</span>
            <div className="w-1 h-4 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-1 h-6 bg-lime-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-1 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            <div className="w-1 h-5 bg-lime-400 rounded-full animate-bounce" style={{ animationDelay: '100ms' }} />
          </div>
        )}

        <div className="relative flex items-center group gap-2">
          <button
            type="button"
            onClick={() => {
              const speechWindow = window as WindowWithSpeech;
              const SpeechClass = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
              if (!SpeechClass) {
                setSpeechSupported(false);
                return;
              }
              if (isListening && recognitionRef.current) {
                recognitionRef.current.stop();
                setIsListening(false);
                return;
              }
              try {
                const rec = new SpeechClass();
                rec.continuous = false;
                rec.interimResults = false;
                rec.onstart = () => setIsListening(true);
                rec.onend = () => setIsListening(false);
                rec.onerror = () => setIsListening(false);
                rec.onresult = (e: BrowserSpeechRecognitionEvent) => {
                  const transcript = e.results[0]?.[0]?.transcript;
                  if (transcript) {
                    setInput(transcript);
                    void sendMessage(transcript);
                  }
                };
                recognitionRef.current = rec;
                rec.start();
              } catch {
                setIsListening(false);
              }
            }}
            title={speechSupported ? 'Neuro-Talk Voice Input' : 'Voice recognition unsupported'}
            className={`p-2.5 rounded-xl border transition-all duration-300 ${
              isListening
                ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.4)] animate-pulse'
                : 'bg-zinc-800/80 border-white/10 text-zinc-400 hover:text-ninpo-lime hover:border-ninpo-lime/40'
            }`}
          >
            <Mic className="w-4 h-4" />
          </button>

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isListening ? "Listening to your voice..." : "Type your message..."}
            className="flex-1 bg-zinc-800/80 border border-white/10 focus:border-ninpo-lime text-zinc-100 placeholder-zinc-500 rounded-xl pl-4 pr-12 py-3 text-[15px] focus:outline-none focus:ring-1 focus:ring-ninpo-lime transition-all duration-200"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="absolute right-2 text-zinc-400 hover:text-ninpo-lime disabled:hover:text-zinc-400 disabled:opacity-50 p-2 transition-colors duration-200"
            aria-label="Send message"
          >
            <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        {(messages.some((m) => m.role === 'agent') || isSupportHumanRequest(input)) && (
          <div className="mt-3 text-center flex justify-center">
            <button
              type="button"
              disabled={isLoading}
              onClick={() => void escalateToHuman('Customer requested teammate assistance')}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-ninpo-lime transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Need more help? Connect with a live agent
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
